import type { ReactNode } from 'react';

/**
 * Stack — vertical or horizontal spacing container. Replaces ad-hoc
 * `space-y-3` / `flex items-center gap-2` / `flex flex-col gap-4`
 * blocks scattered across pages. The whole point is that **the page
 * doesn't decide spacing values** — it picks one of four discrete
 * gap tokens and the design system stays consistent.
 *
 * Two directions:
 *
 *   - `vertical` (default) — column with row gap
 *   - `horizontal` — row with column gap
 *
 * Four gap sizes mapped to the project's spacing scale:
 *
 *   - `xs` →  4px   tight cluster (icon + label, badge + count)
 *   - `sm` →  8px   default for inline groups
 *   - `md` → 12px   default for stacked content within a card
 *   - `lg` → 16px   default for top-level page sections
 *
 * The horizontal variant defaults to `items-center` (the most common
 * case — aligning text + icon on a single baseline). Override with
 * the `align` prop when you need start / stretch / etc.
 *
 * @example
 * // Vertical stack — typical page section
 * <Stack gap="lg">
 *   <SectionA />
 *   <SectionB />
 *   <SectionC />
 * </Stack>
 *
 * // Horizontal cluster — icon + label + count
 * <Stack direction="horizontal" gap="xs">
 *   <Icon name="favorite" size={14} />
 *   <span>{count}</span>
 * </Stack>
 *
 * // Right-aligned button row
 * <Stack direction="horizontal" gap="sm" justify="end">
 *   <CancelButton />
 *   <ConfirmButton />
 * </Stack>
 */
export interface StackProps {
  children: ReactNode;
  /** Layout axis. Default `vertical`. */
  direction?: 'vertical' | 'horizontal';
  /** Gap between children. Default `md` (12px). */
  gap?: 'xs' | 'sm' | 'md' | 'lg';
  /** Cross-axis alignment. For horizontal stacks defaults to
   * `center`; for vertical stacks defaults to `stretch`. */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  /** Main-axis distribution. Default `start`. */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  /** When `true` and direction is `horizontal`, allows children to
   * wrap to a new row when they overflow. Default `false`. */
  wrap?: boolean;
  /** Optional extra Tailwind classes — usually for adding padding,
   * width constraints, or borders. Avoid passing margin / gap /
   * flex-direction here; that's the primitive's job. */
  className?: string;
}

const GAP_VERTICAL: Record<NonNullable<StackProps['gap']>, string> = {
  xs: 'space-y-1',
  sm: 'space-y-2',
  md: 'space-y-3',
  lg: 'space-y-4',
};

const GAP_HORIZONTAL: Record<NonNullable<StackProps['gap']>, string> = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
};

const ALIGN: Record<NonNullable<StackProps['align']>, string> = {
  start:    'items-start',
  center:   'items-center',
  end:      'items-end',
  stretch:  'items-stretch',
  baseline: 'items-baseline',
};

const JUSTIFY: Record<NonNullable<StackProps['justify']>, string> = {
  start:   'justify-start',
  center:  'justify-center',
  end:     'justify-end',
  between: 'justify-between',
  around:  'justify-around',
};

export function Stack({
  children,
  direction = 'vertical',
  gap = 'md',
  align,
  justify = 'start',
  wrap = false,
  className = '',
}: StackProps) {
  if (direction === 'vertical') {
    return (
      <div
        className={`
          flex flex-col
          ${ALIGN[align ?? 'stretch']}
          ${JUSTIFY[justify]}
          ${GAP_VERTICAL[gap]}
          ${className}
        `}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={`
        flex flex-row
        ${ALIGN[align ?? 'center']}
        ${JUSTIFY[justify]}
        ${GAP_HORIZONTAL[gap]}
        ${wrap ? 'flex-wrap' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  );
}
